import { useState } from 'react'
import { useStripe, useElements } from '@stripe/react-stripe-js'

import useModalTakeover from 'src/widgets/useModalTakeover'
import walletEmailError from 'src/widgets/walletEmailError'
import {
  createIntent, finalizeIntent, normalizeStripeError, subscriptionIntervals,
} from './stripeIntents'
import { genericError } from './useRecord'

// A confirmed intent is "done" only in these states. `succeeded`
// covers cards + wallets (and SetupIntents once the mandate/3DS
// clears); `processing` covers ACH bank debits, which settle
// asynchronously and legitimately return here without an error. Any
// other terminal-ish state (requires_action, requires_payment_method,
// requires_confirmation, canceled) means the attempt did NOT complete
// — Stripe can return `requires_action` with no error object when an
// embedded-iframe 3DS modal is blocked from opening (v6 guards this
// case explicitly), and advancing to Thank You there would tell the
// donor a blocked/failed payment succeeded.
const COMPLETED_INTENT_STATUSES = [ 'succeeded', 'processing' ]

// Stripe submit flow: elements.submit → record.saveDraft → createIntent
// → stripe.confirmPayment → finalizeIntent → finished. Drives every
// Stripe-backed payment method on v7:
//   - credit_card / bank: PaymentElement renders the inputs (and the
//     bank flow surfaces the Financial Connections modal + ACH mandate
//     inline). `submit()` is the entry point.
//   - apple_pay / google_pay: ExpressCheckoutElement opens the wallet
//     sheet on click; the donor's selection comes back via onConfirm
//     with billingDetails (name/email/phone). `submitExpressCheckout()`
//     is the entry point.
//
// Both paths share an inner `process()` because once the payment method
// is tokenized, the rest of the flow (saveDraft → createIntent →
// confirmPayment → finalizeIntent) is identical. What differs is where
// the contact info and the `payment_method_used` string come from.
//
// Adapted from v6's fiat/usePayment but trimmed to v7's surface:
//   - no enable_upsell / connected-account confirm leg (Legacy / sympathy
//     card flow, deliberately out of scope per the v7 roadmap)
//   - no plugins
//
// SetupIntent / subscription path IS supported: when the donor picks a
// recurring frequency, createIntent returns intent_type=setup_intent,
// stripe.confirmSetup runs in place of confirmPayment, and the
// finalizeIntent PATCH can come back with `requiresAction` carrying
// `client_secret` if the first subscription invoice needs 3DS — that
// gets handled with stripe.confirmCardPayment on the same Stripe
// instance. (The connected-account flavor of that confirmCardPayment
// — where v6 loadStripe'd a fresh instance with `{ stripeAccount }` —
// stays out of scope here. Per v6's own in-code note at
// useCreditCard.js:149-151, that branch fires only for the upsell
// path, which v7 doesn't carry; reach for that pattern again only if
// v7 ever takes on a connected-account subscription surface.)
//
// The hook owns its own processing + errors state (rather than routing
// errors through a shared App-level handler) so the step components can
// render inline retry feedback without a panel transition. App.jsx's
// `finished` is still called on success to advance the panel and seed
// the thank-you step's donation snapshot.

// 'apple_pay' → 'Apple Pay'. The backend's payment_method_types
// dispatch (StripeIntentsController#payment_method_types) keys off the
// titlecased label, and the order's payment_method_used column stores
// it as a human-readable string for staff/reporting.
const titleizePaymentType = type =>
  (type || '')
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

// PaymentElement-only mapping; wallets get their friendly label via
// titleizePaymentType from the ECE event payload instead.
const paymentMethodUsedFor = paymentMethod =>
  paymentMethod === 'bank' ? 'ACH' : 'Credit Card'

const usePayment = ({
  i18n, donation, record, partnerItem,
  paymentMethod, creditCardProps, bankProps,
  finished, setSubmitInFlight,
}) => {
  const stripe = useStripe()
  const elements = useElements()
  const [ processing, setProcessing ] = useState(false)
  const [ errors, setErrors ] = useState(null)
  // Full-screens the widget's host-page <dialog> while a Stripe overlay
  // modal is up (3DS challenge, or the Financial Connections bank-link
  // picker for ACH) so the cramped-overlay problem goes away. autoListen:
  // the modal can open during PaymentElement interaction (FC) as well as
  // during confirm (3DS), so observe the whole payment step rather than
  // bracketing a single call — it auto-takes-over on a covering overlay
  // and releases when it's removed. No-op unless embedded; opt-in per PWC
  // (data.fullscreen_3ds), so off unless a partner has it switched on.
  useModalTakeover({
    enabled: creditCardProps?.fullscreen_3ds === true,
    autoListen: true,
  })

  const process = async ({
    billingDetails, contact, paymentMethodUsed, paymentMethodKey,
    onEarlyFailure,
  }) => {
    // Re-entrancy / not-ready guard. ECE flows still need to notify
    // the wallet sheet — a second onConfirm fired while the first
    // invocation is still running would otherwise leave the Apple /
    // Google Pay sheet sitting on its "Processing…" spinner until
    // Stripe's internal timeout (~60s). Same goes for the
    // stripe/elements-not-loaded cases. State (processing flag,
    // errors) stays as-is because the first invocation, if any, is
    // still in flight and owns it.
    if (!stripe || !elements || processing) {
      onEarlyFailure?.()
      return
    }
    setProcessing(true)
    setErrors(null)

    // For ECE flows: `onEarlyFailure` is the wallet sheet's
    // event.paymentFailed callback. Anything that bails before we
    // hand control to stripe.confirmPayment must invoke it, otherwise
    // the wallet sheet sits on its "Processing…" spinner until Stripe's
    // internal timeout (~60s) fires. PaymentElement flows pass no
    // callback; the early-bail just resets processing and shows the
    // error inline.
    //
    // setSubmitInFlight(false) tears the App-level processing overlay
    // back down so the donor lands on the still-mounted step component
    // with the inline alert.
    const earlyFail = errors => {
      setProcessing(false)
      setSubmitInFlight(false)
      onEarlyFailure?.()
      if (errors) setErrors(errors)
    }

    // 1. Validate / tokenize via the surrounding Elements scope.
    //    PaymentElement: field-level validation errors render inline,
    //    so we just bail. ExpressCheckoutElement: this is largely a
    //    no-op since the wallet has already tokenized, but Stripe
    //    documents calling it for parity with the manual flow.
    //    Overlay stays off during this stage so PaymentElement's inline
    //    validation errors stay visible to the donor.
    const { error: submitError } = await elements.submit()
    if (submitError) return earlyFail()

    // 2. Everything past elements.submit can take seconds (saveDraft,
    //    createIntent, confirmPayment with 3DS / wallet sheets,
    //    finalize for subscriptions). Cover the panel with the App-
    //    level overlay so the donor doesn't keep tapping a wallet
    //    button or wonder whether the form is hung.
    setSubmitInFlight(true)

    // Errors raised after we drop the wallet sheet via confirmPayment
    // can't usefully invoke event.paymentFailed (the sheet's already
    // gone), so they don't need the onEarlyFailure path — just clear
    // local state, drop the overlay, and surface the error inline.
    const lateFail = errors => {
      setProcessing(false)
      setSubmitInFlight(false)
      if (errors) setErrors(errors)
    }

    // 3. Save the order draft so the backend has a record before we
    //    create the PaymentIntent. payment_method_used drives the
    //    server-side payment_method_types choice (Apple Pay / Google
    //    Pay / Credit Card → card, ACH → us_bank_account). For wallet
    //    flows the contact override carries name/email/phone from the
    //    wallet event; PaymentElement flows leave contact undefined
    //    and the draft picks those up from donation state.
    const { errors: draftErrors, token, serializedDonation } =
      await record.saveDraft({
        contact,
        order: { payment_method_used: paymentMethodUsed },
      })
    if (draftErrors) return earlyFail(draftErrors)

    // 4. Backend creates a PaymentIntent (one-time donation) or a
    //    SetupIntent (recurring — backend will create the Stripe
    //    Subscription on PATCH after we tokenize the payment method).
    //    intentResult.intent_type tells us which way it routed; one_time
    //    and unknown frequencies pass subscription=undefined and get
    //    PaymentIntent.
    // Both credit_card and bank route to the same
    // payment_processors_stripe_intents_path, but the props bundle
    // carrying it differs per method — a bank-only config has no
    // creditCardProps, so read from whichever bundle matches the
    // active method (falling back to the other so a card+bank config
    // still resolves regardless of which the donor picked).
    const stripeIntentsPath =
      (paymentMethod === 'bank' ? bankProps : creditCardProps)?.stripeIntentsPath
      ?? (paymentMethod === 'bank' ? creditCardProps : bankProps)?.stripeIntentsPath
    const intentResult = await createIntent({
      stripeIntentsPath,
      orderId: token,
      locale: i18n.locale,
      subscription: subscriptionIntervals[donation.state.subscription],
      i18n,
    })
    if (intentResult.errors) return earlyFail(intentResult.errors)

    // 5. Confirm with Stripe. redirect: 'if_required' lets us stay
    //    in-page when no 3DS challenge is needed; for 3DS Stripe
    //    handles the modal/redirect itself and resolves back here.
    //    billingDetails is sourced from the Contact step for
    //    PaymentElement flows and from the ECE event for wallets.
    //    SetupIntent (recurring) goes through confirmSetup so the
    //    payment method gets attached to a customer for off-session
    //    reuse rather than charged immediately.
    const isSetupIntent = intentResult.intent_type == 'setup_intent'
    const confirmFn = isSetupIntent ? stripe.confirmSetup : stripe.confirmPayment

    const confirmResult = await confirmFn({
      elements,
      clientSecret: intentResult.client_secret,
      confirmParams: {
        return_url: location.href,
        payment_method_data: { billing_details: billingDetails },
      },
      redirect: 'if_required',
    })
    const intent = confirmResult.paymentIntent || confirmResult.setupIntent
    const confirmError = confirmResult.error
    if (confirmError) {
      setProcessing(false)
      setSubmitInFlight(false)
      // PaymentElement renders card_error (declines, expired cards)
      // and validation_error (field-format issues) inline on the
      // relevant field as soon as the overlay drops, so we don't
      // double them up in the alert. Wallet flows have no inline
      // render once the wallet sheet has closed, so their card-side
      // errors need the alert too. Other error types (3DS auth
      // failures, API / network errors during the redirect leg)
      // always go to the alert.
      const isWallet = paymentMethodKey === 'apple_pay'
        || paymentMethodKey === 'google_pay'
      const handledInline = !isWallet
        && [ 'card_error', 'validation_error' ].includes(confirmError.type)
      if (handledInline) return
      return setErrors(normalizeStripeError(confirmError))
    }

    // confirmFn can resolve with no error but an incomplete intent
    // (see COMPLETED_INTENT_STATUSES) — most importantly requires_action
    // when a 3DS challenge couldn't open in the embedded iframe. Bail
    // before finalize/finished so a blocked or unfinished payment never
    // reaches Thank You. The charge/subscription isn't captured in that
    // state, so no money moved; this is purely about not lying to the
    // donor.
    if (!COMPLETED_INTENT_STATUSES.includes(intent?.status))
      return lateFail(genericError(i18n))

    // 6. PATCH the order with the resolved payment_method_id. For
    //    PaymentIntent flows this is best-effort (charge already
    //    settled). For SetupIntent flows the backend creates the
    //    Stripe::Subscription on this PATCH; if the first invoice
    //    needs 3DS the response carries a client_secret we then
    //    confirm on-session via confirmCardPayment.
    const finalizeResult = await finalizeIntent({
      stripeIntentsPath,
      token,
      paymentMethodId: intent.payment_method?.id || intent.payment_method,
      locale: i18n.locale,
      intentType: intentResult.intent_type,
      i18n,
    })
    if (finalizeResult.errors) return lateFail(finalizeResult.errors)
    if (finalizeResult.requiresAction) {
      // Subscription 3DS challenge on the first invoice. Stripe's
      // confirmCardPayment opens the auth modal; on success the
      // backend resolves the subscription's status from the
      // PaymentIntent (and webhooks back-fill anyway).
      const { paymentIntent: actionIntent, error: actionError } =
        await stripe.confirmCardPayment(
          finalizeResult.requiresAction.client_secret,
        )
      if (actionError) return lateFail(normalizeStripeError(actionError))
      // Same guard as the primary confirm: confirmCardPayment can come
      // back without an error yet still not have cleared the challenge
      // (e.g. auth abandoned). Don't advance to Thank You unless the
      // first-invoice PaymentIntent actually completed.
      if (!COMPLETED_INTENT_STATUSES.includes(actionIntent?.status))
        return lateFail(genericError(i18n))
    }

    // 6. Hand off to App.jsx, which advances to thank_you and seeds
    //    the donation snapshot rendered there.
    const { state, ...rest } = donation
    finished({
      ...state,
      ...rest,
      ...serializedDonation,
      token,
      partnerItem,
      paymentMethod: paymentMethodKey,
    })
  }

  // PaymentElement entry point — credit_card / bank. Contact info comes
  // from the Contact step, kept in donation state. When AddressElement
  // captured a billing address (collectAddress on), include it in
  // billing_details — StripePayment.jsx suppresses the address fields
  // on PaymentElement (`fields.billingDetails.address: 'never'`) when
  // it had defaults to seed, and Stripe requires the suppressed data
  // to be passed at confirm time instead. donation.state.address is
  // already the snake_case shape Stripe expects (city, country, line1,
  // line2, postal_code, state) because AddressElement's onChange
  // populates it that way.
  const submit = () => process({
    billingDetails: {
      name: [
        donation.state.firstName,
        donation.state.lastName,
      ].join(' ').trim(),
      email: donation.state.email || undefined,
      ...(donation.collectAddress && donation.state.address && {
        address: donation.state.address,
      }),
    },
    paymentMethodUsed: paymentMethodUsedFor(paymentMethod),
    paymentMethodKey: paymentMethod,
  })

  // ExpressCheckoutElement entry point — apple_pay / google_pay. The
  // full event is passed in (rather than just billingDetails /
  // expressPaymentType) so we can reach event.paymentFailed when the
  // submit bails before stripe.confirmPayment — without that callback
  // the wallet sheet hangs on its "Processing…" spinner until Stripe's
  // internal timeout fires. The CDN runtime reports the chosen wallet
  // on event.expressPaymentType (the npm types call it
  // paymentMethodType, which is undefined at runtime — see the
  // dedicated memory note); using it as the source of truth means we
  // record what the donor actually paid with even if the picker
  // selection somehow drifted.
  const submitExpressCheckout = event => {
    const { expressPaymentType, billingDetails } = event
    // Wallet sheets return the donor's display name as a single string —
    // but a wallet can omit it (e.g. Apple Pay when no billing contact
    // is requested), and an empty/whitespace name must still yield the
    // 'N/A' fallback the backend requires (user_first_name can't be
    // blank). filter(Boolean) drops the empty token that '' → ['']
    // produces so the `|| 'N/A'` actually fires; a destructuring default
    // ([firstName = 'N/A']) does NOT, since it only triggers on
    // undefined, letting '' through and failing draft creation. Matches
    // v6's `name.at(0) || 'N/A'` guard in fiat/useCreditCard.
    const nameParts = (billingDetails?.name || '').trim().split(/\s+/).filter(Boolean)
    const firstName = nameParts[0] || 'N/A'
    const lastName = nameParts.slice(1).join(' ') || 'N/A'

    // We now request the billing address unconditionally (it's the only
    // way the ECE surfaces the donor's name — see ExpressCheckout's
    // handleEceClick), so it's present for every wallet donation and we
    // store it on the order regardless. Sharing it with the beneficiary
    // is gated separately by the order's `shares` (address_with_beneficiary,
    // granted only when the fundraiser collect_address? — see
    // OrderShareInfo#default_shares), so storing it always doesn't leak
    // it. It's already Stripe's snake_case shape (line1, line2, city,
    // state, postal_code, country), matching donation.state.address, so
    // it flows straight into the draft's order.address (via useRecord's
    // contact.address) and into the confirm billing_details.
    const walletAddress = billingDetails?.address

    // Checked ahead of saveDraft, whose rejection useRecord can only
    // surface as the generic donation_error. Trimmed first and sent
    // trimmed; see walletEmailError.
    const email = billingDetails?.email?.trim()
    const wallet = titleizePaymentType(expressPaymentType)
    const emailError = walletEmailError({ i18n, email, wallet })
    if (emailError) {
      event.paymentFailed?.({ reason: 'fail' })
      return setErrors(emailError)
    }

    return process({
      contact: {
        firstName,
        lastName,
        email,
        phone: billingDetails?.phone,
        ...(walletAddress && { address: walletAddress }),
      },
      billingDetails: {
        name: billingDetails?.name,
        email: email || undefined,
        phone: billingDetails?.phone || undefined,
        ...(walletAddress && { address: walletAddress }),
      },
      paymentMethodUsed: wallet,
      paymentMethodKey: expressPaymentType,
      onEarlyFailure: () => event.paymentFailed?.({ reason: 'fail' }),
    })
  }

  return { submit, submitExpressCheckout, processing, errors }
}

export default usePayment
